import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCheckoutApi } from '../checkout/useCheckoutApi'
import { useRequirePaymentProfile } from '../payment/useRequirePaymentProfile'
import { useRefundPoints } from '../refundPoints/useRefundPoints'

import { useConfirm } from '../../contexts/ConfirmContext'
import { CheckoutType, PassProduct } from '../../types/booking/booking'
import { ConfirmLayout, CONFIRM_SKIP_NAVIGATION } from '../../types/confirm/confirm'
import { PaymentMethod } from '../../types/payment/payment'
import {
  attemptRequestId,
  attemptSignature,
  checkoutPath,
  formatMinutes,
  formatMoney,
  formatPoints,
} from '../../utils/booking/booking'
import { applicablePoints } from '../../utils/booking/refundPoints'

export type PassPurchaseLocationState = { buyPassProductId?: number }

/**
 * Buying a pass is a checkout like a booking: a payment hold with a server deadline, confirmed only by a timely
 * payment. The pass becomes usable once the checkout is confirmed. Refund points pay first; when they cover the whole
 * price the pass is activated without a payment.
 */
export const usePassPurchase = (returnTo = '/passes') => {
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const { create } = useCheckoutApi()
  const { requirePaymentProfile } = useRequirePaymentProfile()
  const { summary } = useRefundPoints()
  const { t } = useTranslation(['passes', 'bookings'])

  const buy = async (product: PassProduct) => {
    let points = 0
    try {
      points = applicablePoints(product.price, (await summary())?.available ?? 0)
    } catch {
      // Previewed without points; the server applies them on checkout anyway.
    }
    const due = Math.round((product.price - points) * 100) / 100
    if (
      due > 0 &&
      !requirePaymentProfile({
        returnTo,
        returnState: { buyPassProductId: product.id } satisfies PassPurchaseLocationState,
      })
    ) {
      return
    }
    const signature = attemptSignature(['pass', product.id, product.versionId])

    confirm({
      layout: ConfirmLayout.DETAILS,
      title: t('passes.purchase.title'),
      subtitle: t('passes.purchase.subtitle'),
      itemCards: [
        {
          id: product.id!,
          title: product.name,
          details: [
            { label: t('passes.card.minutes'), value: formatMinutes(product.includedMinutes) },
            { label: t('passes.card.validity'), value: t('passes.card.validity-days', { days: product.validityDays }) },
          ],
        },
      ],
      summary: {
        details:
          points > 0
            ? [
                {
                  label: t('bookings:booking.booking-courts.summary-panel.total-price'),
                  value: formatMoney(product.price, product.currency),
                },
                {
                  label: t('bookings:booking.booking-courts.summary-panel.refund-points'),
                  value: t('bookings:booking.booking-courts.summary-panel.refund-points-value', {
                    points: formatPoints(points),
                  }),
                },
              ]
            : [],
        total: {
          label: t(
            points > 0
              ? 'bookings:booking.booking-courts.summary-panel.amount-due'
              : 'bookings:booking.booking-courts.summary-panel.total'
          ),
          value: formatMoney(due, product.currency),
        },
      },
      warning: due > 0 ? t('bookings:booking.checkout.hold-notice') : undefined,
      confirmLabel:
        due > 0
          ? t('bookings:booking.checkout.reserve-and-pay', { total: formatMoney(due, product.currency) })
          : t('passes.purchase.confirm'),
      backLabel: t('bookings:booking-confirmation.back-label'),
      showTermsLink: due > 0,
      returnTo,
      onConfirm: async () => {
        const checkout = await create({
          clientRequestId: attemptRequestId(signature),
          type: CheckoutType.PASS_PURCHASE,
          topUpPassProductId: product.id,
          paymentMethod: PaymentMethod.CARD,
        })
        if (checkout) {
          navigate(checkoutPath(checkout.holdId), { replace: true })
        }
        return CONFIRM_SKIP_NAVIGATION
      },
    })
  }

  return { buy }
}
